import { debugLog } from "./utils/debugLog.js";
import React, { useState, useEffect } from "react";
import Notification from "./Notification";
import { Tooltip } from "@material-tailwind/react";
import { setShowNotif, setContents, extractDomain, isManagedClient } from "./helpers";
import { useSignUp, useSignIn, useUser, useClerk } from "@clerk/clerk-react";
import { useNavigate, Link } from "react-router-dom";
import { InfinitySpin } from "react-loader-spinner";
import { StarIcon } from '@heroicons/react/20/solid'
// Removed flowbite carousel - using custom implementation
import brandonMinaImage from "./assets/brandon-mina-linkedin-profile.jpeg";
import Airtable from 'airtable';
import { ensureUserInAirtable } from './services/ensureUserInAirtable';
// import { OAuthStrategy } from "@clerk/types";
// test

const apiKey = process.env.REACT_APP_AIR_KEY;
const baseId = process.env.REACT_APP_AIR_BASEID;
const usersTableId = process.env.REACT_APP_AIR_USERS;

// Airtable base for company lookups
const airtableBase = new Airtable({ apiKey }).base(baseId);

// Personal email domains - these users can sign up but will create "unverified" workspaces
const PERSONAL_EMAIL_DOMAINS = [
	'gmail.com',
	'googlemail.com',
	'yahoo.com',
	'yahoo.ca',
	'yahoo.co.uk',
	'outlook.com',
	'hotmail.com',
	'hotmail.ca',
	'live.com',
	'msn.com',
	'icloud.com',
	'me.com',
	'aol.com',
	'protonmail.com',
	'proton.me',
	'mail.com',
	'zoho.com',
];

const isPersonalEmail = (email) => {
	if (!email) return true;
	const domain = email.split('@')[1]?.toLowerCase();
	return PERSONAL_EMAIL_DOMAINS.includes(domain);
};

function SignInPage() {
	const navigate = useNavigate();
	const { user } = useUser();
	const clerk = useClerk();

	const [userEmail, setUserEmail] = useState("");
	const [userPassword, setUserPassword] = useState("");
	const { isLoaded: isSignInLoaded, signIn, setActive } = useSignIn();
	const [showSignInNotification, setShowSignInNotification] = useState({
		showNotif: false,
		contents: { type: "", heading: "", message: "" },
	});
	const [loadingRedirect, setLoadingRedirect] = useState(false);

	const handleSignIn = async (event) => {
		event.preventDefault();

		if (!isSignInLoaded) return;

		try {
			const result = await signIn.create({
				identifier: userEmail,
				password: userPassword,
			});

			if (result.status === "complete") {
				setLoadingRedirect(true);

				// Activate session first to get user object
				await setActive({ session: result.createdSessionId });
				
				// Look up user in Airtable
				const response = await fetch(
					`https://api.airtable.com/v0/${process.env.REACT_APP_AIR_BASEID}/${process.env.REACT_APP_AIR_USERS}?filterByFormula={Email}='${encodeURIComponent(userEmail.toLowerCase())}'`,
					{
						headers: {
							Authorization: `Bearer ${process.env.REACT_APP_AIR_KEY}`,
							"Content-Type": "application/json",
						},
					}
				);
				
				const data = await response.json();
				let userAirtableId = null;
				let companyId = null;
				let companyDomain = null;
				let companyName = null;
				let workspaceRole = null;
				
				debugLog('Sign-in: Checking Airtable user record for', userEmail);
				debugLog('Sign-in: Found', data.records.length, 'user record(s)');
				
				if (data.records.length > 0) {
					userAirtableId = data.records[0].id;
					const userRecord = data.records[0];
					
					debugLog('Sign-in: User Airtable ID:', userAirtableId);
					debugLog('Sign-in: User Companies field:', userRecord.fields['Companies']);
					
					// Get company ID from user's Companies field (first company) - string or array
					const raw = userRecord.fields['Companies'];
					const companies = !raw ? [] : typeof raw === 'string'
						? raw.split(',').map((s) => s.trim()).filter(Boolean)
						: Array.isArray(raw) ? raw.map((c) => String(c).trim()).filter(Boolean) : [];
					if (companies.length > 0) {
						companyId = companies[0];
						debugLog('Sign-in: Found company ID:', companyId);
						
						// Fetch company details to get Domain, Name, and ManagedClient status
						try {
							const company = await airtableBase('companies').find(companyId);
							if (company.fields) {
								companyDomain = company.fields['Domain'] || null;
								companyName = company.fields['Name'] || null;
								debugLog('Sign-in: Company details - Domain:', companyDomain, 'Name:', companyName, 'ManagedClient:', company.fields['ManagedClient']);
							}
						} catch (companyErr) {
							console.error('Error fetching company details:', companyErr);
						}
					} else {
						debugLog('Sign-in: User has no companies - will check email type and route accordingly');
					}
					
					// Store Airtable ID and company ID in Clerk user metadata
					// Get user object from Clerk after setActive
					try {
						// Wait a moment for user object to be available after setActive
						await new Promise(resolve => setTimeout(resolve, 300));
						
						// Get user from Clerk
						const clerkUser = await clerk.user?.reload ? await clerk.user.reload() : clerk.user;
						if (clerkUser) {
							await clerkUser.update({
								publicMetadata: {
									...clerkUser.publicMetadata,
									airtable_id: userAirtableId,
									airtableId: userAirtableId,
									company_id: companyId,
									company_domain: companyDomain,
									company_name: companyName,
								}
							});
							debugLog('Sign-in: Updated Clerk metadata with company info');
						}
					} catch (err) {
						console.error('Sign-in: Error updating Clerk metadata:', err);
						// Continue anyway - metadata update is not critical for redirect
					}
				} else {
					debugLog('Sign-in: No Airtable user record found for', userEmail, '- creating new user via ensureUserInAirtable');
					
					const userEmailLower = userEmail.toLowerCase();
					const isPersonal = isPersonalEmail(userEmailLower);
					
					// Get full name from Clerk for new user record
					let fullName = '';
					try {
						const clerkUser = await clerk.user?.reload ? await clerk.user.reload() : clerk.user;
						fullName = clerkUser?.fullName || '';
					} catch (err) {
						console.error('Error getting Clerk user info:', err);
					}

					if (process.env.REACT_APP_DEBUG_AUTH === 'true') {
						debugLog('[SignInPage] handleSignIn: calling ensureUserInAirtable', { email: userEmailLower.slice(0, 3) + '***' });
					}
					const result = await ensureUserInAirtable({
						email: userEmailLower,
						fullName,
						airtableBase,
					});

					if (process.env.REACT_APP_DEBUG_AUTH === 'true') {
						debugLog('[SignInPage] ensureUserInAirtable result', { userAirtableId: !!result.userAirtableId });
					}
					if (result.userAirtableId) {
						userAirtableId = result.userAirtableId;
						companyId = result.companyId;
						companyDomain = result.companyDomain || extractDomain(userEmailLower);
						companyName = result.companyName;
						workspaceRole = companyId ? 'Member' : null;
						
						debugLog('Sign-in: Created new Airtable user:', userAirtableId);
						
						// Update Clerk metadata with new Airtable ID
						try {
							const clerkUser = await clerk.user?.reload ? await clerk.user.reload() : clerk.user;
							if (clerkUser) {
								await clerkUser.update({
									publicMetadata: {
										...clerkUser.publicMetadata,
										airtable_id: userAirtableId,
										airtableId: userAirtableId,
										company_id: companyId,
										company_domain: companyDomain,
										workspace_role: workspaceRole,
										is_personal_email: isPersonal,
									}
								});
								debugLog('Sign-in: Updated Clerk metadata with new user info');
							}
						} catch (metadataErr) {
							console.error('Sign-in: Error updating Clerk metadata:', metadataErr);
						}
					} else {
						console.error('Sign-in: ensureUserInAirtable failed to create user');
					}
				}

				const emailDomain = extractDomain(userEmail);
				setLoadingRedirect(false);

				// Send sign-in Slack notification (fire-and-forget); skip failure for rate_limit
				const shouldAlertFailure = !userAirtableId && result?.errorReason !== 'rate_limit';
				if (userAirtableId || shouldAlertFailure) {
					fetch('/api/auth-notification', {
						method: 'POST',
						headers: { 'Content-Type': 'application/json' },
						body: JSON.stringify({
							email: userEmail.toLowerCase(),
							type: userAirtableId ? 'signin' : 'failure',
							companyName: companyName || undefined,
							domain: companyDomain || emailDomain || undefined,
							reason: userAirtableId ? undefined : 'ensureUserInAirtable returned null on sign-in',
						}),
					}).catch(err => console.error('Auth notification error:', err));
				}

				// Get redirect URL from query parameters
				const params = new URLSearchParams(window.location.search);
				const redirectUrl = params.get('redirect');
				
				// Determine navigation based on company and ManagedClient status
				if (companyId) {
					// User has a company - check if it's a managed client
					try {
						const company = await airtableBase('companies').find(companyId);
						const isManaged = isManagedClient(company);
						
						if (isManaged) {
							// Managed client - go directly to main app (skip onboarding)
							debugLog('Sign-in: Managed client detected, going to main app');
							navigate(redirectUrl || `/grants-section?website=${encodeURIComponent(companyDomain || emailDomain)}&company=${encodeURIComponent(companyName || '')}`);
						} else {
							// Not a managed client - go to onboarding or requested redirect
							debugLog('Sign-in: Not a managed client, going to onboarding');
							navigate(
								redirectUrl ||
									`/onboarding-v2?company=${encodeURIComponent(companyId)}`
							);
						}
					} catch (companyErr) {
						console.error('Sign-in: Error checking ManagedClient status:', companyErr);
						// On error, default to onboarding
						navigate(
							redirectUrl ||
								`/onboarding-v2?company=${encodeURIComponent(companyId)}`
						);
					}
				} else {
					// No company found - check if personal or work email
					const userEmailLower = userEmail.toLowerCase();
					const isPersonal = isPersonalEmail(userEmailLower);
					
					if (isPersonal) {
						// Personal email - go to workspace selection
						debugLog('Sign-in: Personal email, no company - going to workspace selection');
						navigate('/workspace-selection');
					} else {
						// Work email but no company - should have been created above, but if not, go to workspace selection
						debugLog('Sign-in: Work email but no company found - going to workspace selection');
						navigate('/workspace-selection');
					}
				}
			} else {
				debugLog(result.status);
				let contents = { type: "error", heading: "Error Signing In", message: "Incorrect Email or Password" };
				setContents(contents, setShowSignInNotification);
				setShowNotif(true, setShowSignInNotification);
			}
		} catch (error) {
			setLoadingRedirect(false);
			console.error(error);
			let contents = { type: "error", heading: "Error Signing In", message: error?.errors[0]?.message };
			setContents(contents, setShowSignInNotification);
			setShowNotif(true, setShowSignInNotification);
		}
	};

	const signInWith = (strategy) => {
		if (!isSignInLoaded) return;

		// Get redirect URL from query parameters
		const params = new URLSearchParams(window.location.search);
		const redirectUrl = params.get('redirect');
		const needsOnboarding = params.get('onboarding') === 'true';
		if (redirectUrl) {
			sessionStorage.setItem('grantPageRedirectAfterOAuth', redirectUrl);
		}

		// All OAuth sign-ins go through check_oauth_google which handles workspace routing
		const completeRedirectUrl = needsOnboarding 
			? '/check_oauth_google?onboarding=true' 
			: '/check_oauth_google';

		signIn.authenticateWithRedirect({
			strategy,
			redirectUrl: '/sso-callback',
			redirectUrlComplete: completeRedirectUrl,
		});
	};

  // Show friendly message when redirected from failed sign-up
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const error = params.get('error');
    if (error === 'already_exists') {
      setContents(
        { type: 'info', heading: 'Already have an account?', message: "It looks like you already have an account. Please sign in below or use Continue with Google." },
        setShowSignInNotification
      );
      setShowNotif(true, setShowSignInNotification);
      params.delete('error');
      const newSearch = params.toString();
      window.history.replaceState({}, '', newSearch ? `?${newSearch}` : window.location.pathname);
    } else if (error === 'session_expired') {
      setContents(
        { type: 'info', heading: 'Sign in to continue', message: "Your sign-up session may have expired. If you just created an account, sign in below with your email and password. Otherwise, try signing up again." },
        setShowSignInNotification
      );
      setShowNotif(true, setShowSignInNotification);
      params.delete('error');
      const newSearch = params.toString();
      window.history.replaceState({}, '', newSearch ? `?${newSearch}` : window.location.pathname);
    }
  }, []);

  useEffect(() => {
	const carouselElement = document.getElementById('carousel-testimonials');
	const items = [
				{
					position: 0,
					el: document.getElementById('carousel-indicator-0'),
				},
				{
					position: 1,
					el: document.getElementById('carousel-indicator-1'),
				},
				{
					position: 2,
					el: document.getElementById('carousel-indicator-2'),
				},
			];
		
	const options = {
		defaultPosition: 0,
		interval: false,
		indicators: {
			activeClasses: 'bg-gray-900 z-20',
			inactiveClasses: 'bg-gray-900 z-10',
			items: items,
		},
	};

	const instanceOptions = {
		id: 'carousel-testimonials',
		override: true
	};

	  // Carousel removed - using static display instead
		}, []);

const testimonials = [
	{
		stars: 5,
		text: "Seriously, do this today.\nThese grants & credits expire.\nOnce they're gone, they're gone.",
		author: "Brandon Mina",
		title: "CEO, BrandPilot AI | (CSE: BPAI | OTCQB: BPAIF | FSE:8LHO)",
		image: brandonMinaImage,
	},
	{
		stars: 5,
		text: "Upgraded found us $64,000/yr in savings on our first payroll cycle - we had no revenue and only 2 employees. Magic",
		author: "Matt Parson",
		title: "Founder, Benji",
		image: "https://assets-global.website-files.com/655000b5d6f9d81d7029fd25/65f0a34f691a72257fea9cfb_matt-parson-portrait-p-500.jpeg",
	},
	{
		stars: 5,
		text: "We hired the best CPA in Vancouver to look for credits. Upgraded still blew them out of the water.",
		author: "Hannah Simmons",
		title: "Founder, ERA Brazil",
		image: "https://assets-global.website-files.com/634f60226f66af42a384d5b7/6369789c70e85f8693c7c324_hannah-simmons-profile.jpeg",
	},
	{
		stars: 5,
		text: "We raised $100M from the best VCs. Upgraded still found us credits nobody had ever mentioned.",
		author: "David McMurchy",
		title: "Sales Director, i-Sight",
		image: "https://assets-global.website-files.com/655000b5d6f9d81d7029fd25/65f1325f2d57b8c9b95895ee_david-headshot-1.jpeg",
	},
];

	return (
		<>
			<div className="flex min-h-full flex-1 h-screen overflow-hidden">
				<div className="flex flex-1 flex-col justify-center px-4 py-12 sm:px-6 lg:flex-none lg:px-20 xl:px-24">
					<div className="mx-auto w-full max-w-sm lg:w-96">
						<div>
							<img
								className="h-11 w-auto max-w-[200px] object-contain"
								src="/upgraded-full-logo.png"
								alt="Upgraded"
							/>
							<h2 className="mt-8 text-2xl font-bold leading-9 tracking-tight text-gray-900 text-left">Free Money, Found</h2>
							<p className="mt-2 text-sm leading-6 text-gray-500">
								Not a member?{"  "}
								<a href="/sign-up" className="font-semibold text-sky-600 hover:text-sky-700 transition-colors duration-150">
									Sign Up Instead
								</a>
							</p>
						</div>

						<div className="mt-10">
							<div>
								<div className="mt-10">
									<div className="grid grid-cols-1 gap-4">
										{/* <Tooltip content="Coming Soon" placement="top"> */}
										<button 
											className="flex w-full items-center justify-center gap-3 rounded-md bg-white px-3 py-3 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus-visible:ring-transparent transition-colors"
											onClick={() => {
												signInWith("oauth_google")
											}}
										>
											<svg className="h-5 w-5" aria-hidden="true" viewBox="0 0 24 24">
												<path
													d="M12.0003 4.75C13.7703 4.75 15.3553 5.36002 16.6053 6.54998L20.0303 3.125C17.9502 1.19 15.2353 0 12.0003 0C7.31028 0 3.25527 2.69 1.28027 6.60998L5.27028 9.70498C6.21525 6.86002 8.87028 4.75 12.0003 4.75Z"
													fill="#EA4335"
												/>
												<path
													d="M23.49 12.275C23.49 11.49 23.415 10.73 23.3 10H12V14.51H18.47C18.18 15.99 17.34 17.25 16.08 18.1L19.945 21.1C22.2 19.01 23.49 15.92 23.49 12.275Z"
													fill="#4285F4"
												/>
												<path
													d="M5.26498 14.2949C5.02498 13.5699 4.88501 12.7999 4.88501 11.9999C4.88501 11.1999 5.01998 10.4299 5.26498 9.7049L1.275 6.60986C0.46 8.22986 0 10.0599 0 11.9999C0 13.9399 0.46 15.7699 1.28 17.3899L5.26498 14.2949Z"
													fill="#FBBC05"
												/>
												<path
													d="M12.0004 24.0001C15.2404 24.0001 17.9654 22.935 19.9454 21.095L16.0804 18.095C15.0054 18.82 13.6204 19.245 12.0004 19.245C8.8704 19.245 6.21537 17.135 5.2654 14.29L1.27539 17.385C3.25539 21.31 7.3104 24.0001 12.0004 24.0001Z"
													fill="#34A853"
												/>
											</svg>
											<span className="text-sm font-semibold leading-6">Continue with Google</span>
										</button>
										<p className="text-center text-xs text-gray-500 mt-2">
											Recommended for Gmail or personal email
										</p>
									</div>
								</div>
								<div className="relative mt-5 mb-4">
									<div className="absolute inset-0 flex items-center" aria-hidden="true">
										<div className="w-full border-t border-gray-200" />
									</div>
									<div className="relative flex justify-center text-sm font-medium leading-6">
										<span className="bg-white px-3 text-gray-500">OR</span>
									</div>
								</div>
								<form onSubmit={handleSignIn} className="space-y-6">
									<div>
										<label htmlFor="email" className="block text-sm font-medium leading-6 text-gray-900">
											Work email address
										</label>
										<div className="mt-2">
											<input
												id="email"
												name="email"
												type="email"
												autoComplete="email"
												value={userEmail}
												onChange={(e) => setUserEmail(e.target.value)}
												required
												className="block w-full rounded-md border-0 py-1.5 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
											/>
										</div>
									</div>

									<div>
										<label htmlFor="password" className="block text-sm font-medium leading-6 text-gray-900">
											Password
										</label>
										<div className="mt-2">
											<input
												id="password"
												name="password"
												type="password"
												required
												autoComplete="current-password"
												value={userPassword}
												onChange={(e) => setUserPassword(e.target.value)}
												className="block w-full rounded-md border-0 py-1.5 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
											/>
										</div>
									</div>

									<div>
										{isSignInLoaded && !loadingRedirect ? (
											<button
												disabled={!isSignInLoaded || loadingRedirect}
												type="submit"
												className="flex w-full justify-center rounded-md bg-gray-900 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:opacity-75 transition-opacity duration-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#849E96] shadow-lg"
											>
												Sign In
											</button>
										) : (
											<div className="flex text-center items-center justify-center mr-6">
												<InfinitySpin visible={true} width="100" color="black" ariaLabel="infinity-spin-loading" />
											</div>
										)}
									</div>
									<div className="">
                    {/* <div className="flex items-center">
                      <input
                        id="remember-me"
                        name="remember-me"
                        type="checkbox"
                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                      />
                      <label htmlFor="remember-me" className="ml-3 block text-sm leading-6 text-gray-700">
                        Remember me
                      </label>
                    </div> */}

                    <div className="text-sm leading-6">
											<Link to="/forgot-password" className="font-semibold text-[#849E96] hover:opacity-75 transition-opacity duration-300">
												Forgot password?
											</Link>
										</div>
									</div>
								</form>
							</div>
						</div>
					</div>
				</div>
				<div className="relative hidden w-0 flex-1 lg:block my-auto py-auto h-screen overflow-hidden">
					{/* Beautiful palm frond background image */}
					<div 
						className="absolute inset-0 bg-cover bg-center bg-no-repeat"
						style={{
							backgroundImage: "url('https://images.pexels.com/photos/2781914/pexels-photo-2781914.jpeg')"
						}}
					/>
					{/* Overlay for better text readability */}
					<div className="absolute inset-0 bg-black/20" />
					
					<section className="relative px-6 py-8 sm:py-12 lg:px-8 h-full flex flex-col justify-center">
						<figure className="mx-auto max-w-2xl">
							<div id="carousel-testimonials" className="relative w-full mb-12 z-50" data-carousel="static">
								<div className="relative h-40 overflow-hidden rounded-lg sm:h-48 xl:h-56 2xl:h-64">
									{testimonials.map((testimonial, i) => (
										<div 
											key={i} 
											className="hidden duration-700 ease-in-out absolute inset-0 transition-all transform"
											data-carousel-item
											id={`carousel-indicator-${i}`}
										>
											<div className="absolute inset-0 bg-black/40 backdrop-blur-sm rounded-lg">
												<p className="sr-only">{testimonial.stars} out of 5 stars</p>
												<div className="flex gap-x-1 pt-3 pl-3 text-yellow-400">
													{Array.from({ length: testimonial.stars }).map((_, starIndex) => (
														<StarIcon key={starIndex} className="h-3 w-3 flex-none" aria-hidden="true" />
													))}
												</div>
												<blockquote className="mt-4 pl-3 leading-5 tracking-tight text-white text-base sm:text-lg sm:leading-6 whitespace-pre-line">
													<p>{testimonial.text}</p>
												</blockquote>
												<figcaption className="mt-4 flex pl-3 items-center gap-x-3">
													<img
														className="h-8 w-8 rounded-full bg-gray-900"
														src={testimonial.image}
														alt={testimonial.author}
													/>
													<div className="text-xs leading-4">
														<div className="font-semibold pl-1 text-white">{testimonial.author}</div>
														<div className="mt-0.5 pl-1 text-gray-200">{testimonial.title}</div>
													</div>
												</figcaption>
											</div>
										</div>
									))}
								</div>
							</div>
							{/* Centered text with adjusted sizing and positioning */}
							<div className="flex flex-col gap-6 justify-center items-center -mt-48">
								{[
									{
										value: "+$225,283/yr",
										label: "Secured on Average"
									},
									{
										value: "+1,778",
										label: "Grants & Government Programs"
									},
									{
										value: "+$2.9B/yr",
										label: "Funding Available"
									}
								].map((stat, index) => (
									<div 
										key={index}
										className="group text-center transition-all duration-300
												   border-b border-white/40 pb-3
												   hover:border-white/60"
									>
										<div 
											className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight"
											style={{ fontFamily: "'Playfair Display', serif" }}
										>
											{stat.value}
										</div>
										<div 
											className="mt-1 text-white/95 text-base sm:text-lg lg:text-xl font-medium"
											style={{ fontFamily: "'Crimson Text', serif" }}
										>
											{stat.label}
										</div>
									</div>
								))}
							</div>
						</figure>
					</section>
				</div>
			</div>

			<Notification
				showNotif={showSignInNotification.showNotif}
				setShowNotif={(val) => setShowNotif(val, setShowSignInNotification)}
				contents={showSignInNotification.contents}
			/>
		</>
	);
}

export default SignInPage;
